import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { pageTitle } from '../../helper';
import Div from '../Div';
import PageHeading from '../PageHeading';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';
import ContactInfoWidget from '../Widget/ContactInfoWidget';

export default function ContactPage() {
  pageTitle('Contact Us');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    projectType: '',
    message: '',
  });

  // Field-specific validation errors
  const [errors, setErrors] = useState({});

  // Separate server-level error (e.g., API failure)
  const [serverError, setServerError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // -------------------------
  // Validation Function
  // -------------------------
  const validate = () => {
    const newErrors = {};

    // Full Name
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Mobile
    const numericPhone = formData.mobile.replace(/\D/g, '');
    if (!numericPhone) {
      newErrors.mobile = 'Mobile number is required';
    } else if (numericPhone.length < 8) {
      newErrors.mobile = 'Mobile number is too short';
    }

    // Message
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 5) {
      newErrors.message = 'Message should be at least 5 characters long';
    }

    return newErrors;
  };

  // -------------------------
  // Event Handlers
  // -------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field-specific error
    setErrors((prev) => ({ ...prev, [name]: '' }));
    // Clear server error if user edits fields again
    setServerError('');
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, mobile: value }));
    setErrors((prev) => ({ ...prev, mobile: '' }));
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Reset server error before sending
    setServerError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setServerError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setServerError('There was an error submitting the form. Please try again later.');
    }
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <>
      <PageHeading
        title="Contact Us"
        bgSrc="/images/contact_hero_bg.jpeg"
        pageLinkText="Contact"
      />
      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="row">
          <Div className="col-lg-6">
            <SectionHeading
              title="Do you have a project <br/>in your mind?"
              subtitle="Getting Touch"
            />
            <Spacing lg="55" md="30" />
            <ContactInfoWidget withIcon />
            <Spacing lg="0" md="50" />
          </Div>
          <Div className="col-lg-6">
            {!submitted ? (
              <form onSubmit={handleSubmit} className="row">
                {/* Show server error at the top if any */}
                {serverError && (
                  <div
                    style={{
                      marginBottom: '20px',
                      padding: '10px',
                      color: '#b10000',
                      backgroundColor: '#ffe5e5',
                      border: '1px solid #b10000',
                    }}
                  >
                    {serverError}
                  </div>
                )}

                {/* Full Name */}
                <Div className="col-sm-6">
                  <label className="cs-secondary_color">Full Name*</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="cs-form_field"
                  />
                  {errors.fullName && (
                    <p style={{ color: 'red' }}>{errors.fullName}</p>
                  )}
                  <Spacing lg="20" md="20" />
                </Div>

                {/* Email */}
                <Div className="col-sm-6">
                  <label className="cs-secondary_color">Email*</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="cs-form_field"
                  />
                  {errors.email && (
                    <p style={{ color: 'red' }}>{errors.email}</p>
                  )}
                </Div>

                {/* Mobile (React Phone Input) */}
                <Div className="col-sm-6">
                  <label className="cs-secondary_color">Mobile*</label>
                  <ReactPhoneInput
                    country=""
                    value={formData.mobile}
                    onChange={handlePhoneChange}
                    placeholder="Enter your mobile number"
                    inputStyle={{
                      width: '100%',
                      height: '40px',
                    }}
                  />
                  {errors.mobile && (
                    <p style={{ color: 'red' }}>{errors.mobile}</p>
                  )}
                </Div>

                {/* Project Type (Optional) */}
                <Div className="col-sm-6">
                  <label className="cs-secondary_color">Project Type</label>
                  <input
                    type="text"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className="cs-form_field"
                  />
                </Div>

                {/* Message */}
                <Div className="col-sm-12">
                  <label className="cs-secondary_color">Message*</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    cols="30"
                    rows="7"
                    className="cs-form_field"
                  ></textarea>
                  {errors.message && (
                    <p style={{ color: 'red' }}>{errors.message}</p>
                  )}
                </Div>
                <Spacing lg="55" md="30" />
                {/* Submit Button */}
                <Div className="col-sm-12">
                  <button className="cs-btn cs-style1" type="submit">
                    <span>Send Message</span>
                    <Icon icon="bi:arrow-right" />
                  </button>
                </Div>
              </form>
            ) : (
              <h3 style={{ color: 'black' }}>
                Thank you for your message! We will contact you soon.
              </h3>
            )}
          </Div>
        </Div>
      </Div>
    </>
  );
}
